import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useToast } from "../../../../hooks/toast";
import { getFormulario, salvarLote, mensagemErroParametros } from "../../../../services/parametrosApi";
import { calcularValoresParaSalvar, chaveDe, valorMudou, valoresAtuaisDoFormulario } from "./calcularValoresParaSalvar";

// Estado do formulário por assunto (H1, PO1-3259): carrega o catálogo do escopo com
// os valores do contexto, guarda só o que o usuário alterou (mapa idParametro → valor;
// Id e não nome, porque o catálogo tem nomes repetidos entre módulos),
// salva em lote apenas o diff e recarrega. Mensagens por toast, no padrão do Hub.
export default function useFormularioParametros(escopo, contextoId) {
  const { addToast } = useToast();
  const [formulario, setFormulario] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [alterados, setAlterados] = useState({});
  const [salvando, setSalvando] = useState(false);
  const montadoRef = useRef(true);

  useEffect(() => {
    montadoRef.current = true;
    return () => {
      montadoRef.current = false;
    };
  }, []);

  const notify = useCallback(
    (type, title, description) =>
      addToast({
        place: "tr",
        type,
        title,
        description,
        icon: type === "success" ? "tim-icons icon-check-2" : type === "danger" ? "tim-icons icon-alert-circle-exc" : "tim-icons icon-bell-55",
      }),
    [addToast]
  );

  const recarregar = useCallback(async () => {
    setAlterados({});
    if (!escopo || !contextoId) {
      setFormulario([]);
      setErro("");
      return;
    }
    setCarregando(true);
    setErro("");
    try {
      const data = await getFormulario(escopo, contextoId);
      if (!montadoRef.current) return;
      setFormulario(data);
    } catch (e) {
      if (!montadoRef.current) return;
      const mensagem = mensagemErroParametros(e, "Não foi possível carregar os parâmetros.");
      setErro(mensagem);
      setFormulario([]);
      notify("danger", "Erro ao carregar", mensagem);
    } finally {
      if (montadoRef.current) setCarregando(false);
    }
  }, [escopo, contextoId, notify]);

  useEffect(() => {
    recarregar();
  }, [recarregar]);

  const porId = useMemo(() => {
    const mapa = {};
    formulario.forEach((p) => {
      const chave = chaveDe(p);
      if (chave) mapa[chave] = p;
    });
    return mapa;
  }, [formulario]);

  // Voltar ao valor gravado tira o campo da lista de alterados (o contador fica honesto).
  const setValor = useCallback(
    (idParametro, valor) => {
      const chave = String(idParametro);
      setAlterados((prev) => {
        const param = porId[chave];
        const next = { ...prev };
        if (param && !valorMudou(param, valor)) delete next[chave];
        else next[chave] = valor;
        return next;
      });
    },
    [porId]
  );

  const descartar = useCallback(() => setAlterados({}), []);

  const salvar = useCallback(async () => {
    const valores = calcularValoresParaSalvar(formulario, alterados);
    if (!valores.length) {
      notify("info", "Nada a salvar", "Nenhum parâmetro foi alterado.");
      setAlterados({});
      return null;
    }
    setSalvando(true);
    try {
      const resultado = await salvarLote(escopo, contextoId, valores);
      const gravados = Number(resultado && resultado.gravados) || 0;
      const removidos = Number(resultado && resultado.removidos) || 0;
      const partes = [];
      if (gravados) partes.push(`${gravados} gravado${gravados === 1 ? "" : "s"}`);
      if (removidos) partes.push(`${removidos} de volta ao padrão`);
      notify(
        "success",
        "Parâmetros salvos",
        partes.length ? partes.join(" · ") : `${valores.length} alteração${valores.length === 1 ? "" : "ões"} aplicada${valores.length === 1 ? "" : "s"}.`
      );
      await recarregar();
      return resultado;
    } catch (e) {
      notify("danger", "Erro ao salvar", mensagemErroParametros(e, "Não foi possível salvar os parâmetros."));
      return null;
    } finally {
      if (montadoRef.current) setSalvando(false);
    }
  }, [formulario, alterados, escopo, contextoId, notify, recarregar]);

  const valoresAtuais = useMemo(() => valoresAtuaisDoFormulario(formulario, alterados), [formulario, alterados]);
  const totalAlterados = Object.keys(alterados).length;

  return {
    carregando,
    erro,
    formulario,
    alterados,
    totalAlterados,
    valoresAtuais,
    setValor,
    descartar,
    salvar,
    salvando,
    recarregar,
  };
}
